/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the progress screen for an activity being digitised.
 * @state-owner dataset store
 * @allowed-imports React; workspace store; shared components and hooks; module actions
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects navigation and the simulated parse failure
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { WrapPage } from '../../../shared/components/PageShell';
import type { ActivityRecord } from '../../../shared/lib/model';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useActivityActions } from '../model/use-activity-actions';

export function ActivityParsing({ activity }: { activity: ActivityRecord }) {
  const t = useTranslator();
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const actions = useActivityActions();
  const percent = activity.parsedPercent ?? 0;

  return (
    <WrapPage maxWidth={720}>
      <div className="hd">
        <div>
          <h1>{activity.name}</h1>
          <p className="sub">{t('解析中，可以先離開這一頁')}</p>
        </div>
      </div>
      <section className="panel">
        <header>
          <h2>{t('進度')}</h2>
          <span className="n">{`${percent}%`}</span>
        </header>
        <span className="mtr" style={{ height: 8 }}>
          <i style={{ width: `${percent}%` }} />
        </span>
        <p
          style={{
            fontSize: 15,
            lineHeight: '25px',
            color: 'var(--on-surface-variant)',
            margin: '18px 2px 0',
          }}
        >
          {`正在辨識版面與題目邊界。${
            activity.answerSource === 'ai'
              ? t('這一份沒有附答案，答案會由 AI 生成，每一題都會標成待確認。')
              : `${t('已附答案檔 ')}${activity.answerFileName ?? ''}，會照答案產生詳解。`
          }`}
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="bt" type="button" onClick={() => navigate('act', null)}>
            {t('回教材清單')}
          </button>
          <button className="bt" type="button" onClick={() => actions.failParse(activity.id)}>
            {t('模擬解析失敗')}
          </button>
        </div>
      </section>
    </WrapPage>
  );
}
